import {
  Anchor,
  Dialog,
  isWeb,
  SizableText,
  styled,
  Unspaced,
  useMedia,
  XStack,
  YStack,
} from '@mythical/ui'
import {
  IconClose,
  IconEmailCircle,
  IconFacebookCircle,
  IconRedditCircle,
  IconTwitterCircle,
} from 'app/components/primitive/Icon'
import React from 'react'
import { Pressable } from 'react-native'

import { ShareModalProps } from './ShareModalProps'

const links = [
  {
    icon: IconFacebookCircle,
    label: 'Facebook',
    href: 'https://www.facebook.com/sharer/sharer.php?u=',
  },
  {
    icon: IconTwitterCircle,
    label: 'Twitter',
    href: 'https://twitter.com/intent/tweet?url=',
  },
  {
    icon: IconRedditCircle,
    label: 'Reddit',
    href: 'https://www.reddit.com/submit?url=',
  },
  {
    icon: IconEmailCircle,
    label: 'Email',
    href: 'mailto:?subject=Check out this Mythical Society video&body=',
  },
]

const CopyField = styled(XStack, {
  position: 'relative',
  ai: 'center',
  py: '$2',
  px: '$3',
  bg: '$secondary10',
  flexShrink: 1,
  borderWidth: 1,
  borderColor: '$secondary8',
  borderRadius: '$3',
  space: '$2',
  jc: 'space-between',
  maxWidth: 600,
  w: '100%',
  mx: 'auto',
})

const ShareModal = ({ trigger, href }: ShareModalProps) => {
  const [copied, setCopied] = React.useState(false)
  const media = useMedia()

  const handleSelect = () => {
    if (!isWeb) return
    const textEl = document.getElementById('share-url')
    if (!textEl) return
    const r = document.createRange()
    r.selectNode(textEl)
    window?.getSelection()?.removeAllRanges()
    window?.getSelection()?.addRange(r)
    document.execCommand('copy')

    setCopied(true)
    setTimeout(() => {
      setCopied(false)
    }, 1500)
  }

  return (
    <Dialog>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Adapt when="xxs">
        <Dialog.Sheet
          zIndex={200000}
          snapPoints={[30, 35]}
          modal
          dismissOnSnapToBottom
        >
          <Dialog.Sheet.Overlay animation="quick" bg="$backdrop" o={0.33} />
          <Dialog.Sheet.Frame padding="$0" bg="$secondary10">
            <Dialog.Sheet.Handle bg="$secondary6" />
            <Dialog.Adapt.Contents />
          </Dialog.Sheet.Frame>
        </Dialog.Sheet>
      </Dialog.Adapt>

      <Dialog.Portal>
        <Dialog.Overlay
          key="overlay"
          animation="quick"
          bg="$backdrop"
          o={0.33}
          enterStyle={{ o: 0 }}
          exitStyle={{ o: 0 }}
        />
        <Dialog.Content
          borderRadius="$4"
          elevate
          p="$0"
          key="content"
          overflow="hidden"
          maxWidth={700}
          w="100%"
          mx="$2"
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          x={0}
          scale={1}
          opacity={1}
          y={0}
        >
          <YStack>
            <XStack jc="center" p="$2.5" bg="$secondary10">
              <Dialog.Title fontFamily="$body" color="$primary1" size="$5">
                Share via...
              </Dialog.Title>
              <Unspaced>
                <Dialog.Close cursor="pointer" pos="absolute" right="$2">
                  <IconClose size={24} color="$primary1" p="$1" />
                </Dialog.Close>
              </Unspaced>
            </XStack>
            <YStack space="$3" bg="$secondary9" p="$4">
              <XStack
                jc="flex-start"
                space="$3"
                py="$2"
                className="overflow-x-auto"
                $gtXxs={{
                  jc: 'center',
                }}
              >
                {links.map((item) => {
                  const Icon = item.icon
                  return (
                    <YStack
                      animation="quick"
                      key={item.href}
                      scale={1}
                      hoverStyle={{
                        scale: 1.05,
                      }}
                      pressStyle={{
                        scale: 0.95,
                      }}
                    >
                      <Anchor
                        key={item.label}
                        display="flex"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`${item.href}${href}`}
                        fd="column"
                        jc="center"
                      >
                        <Icon
                          size={74}
                          mb="$1"
                          {...(media.xs && { size: 64 })}
                        />
                        <SizableText
                          textAlign="center"
                          color="$primary1"
                          fontSize="$3"
                        >
                          {item.label}
                        </SizableText>
                      </Anchor>
                    </YStack>
                  )
                })}
              </XStack>
              <Pressable onPress={handleSelect}>
                <CopyField>
                  <SizableText
                    id="share-url"
                    w="calc(100% - 60px)"
                    className="overflow-x-auto hide-scrollbar"
                    color="$secondary5"
                    fontSize="$3"
                    whiteSpace="nowrap"
                  >
                    {href}
                  </SizableText>
                  <Unspaced>
                    <SizableText
                      position="absolute"
                      right="$3"
                      top="50%"
                      y="-50%"
                      color={copied ? '$green6' : '$primary6'}
                      fontSize="$3"
                      textTransform="uppercase"
                      textAlign="center"
                      hoverStyle={{
                        color: copied ? '$green6' : '$primary1',
                      }}
                      animation="quick"
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </SizableText>
                  </Unspaced>
                </CopyField>
              </Pressable>
            </YStack>
          </YStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  )
}

export default ShareModal
